import pdfMake from "pdfmake/build/pdfmake";
// pdfmake v0.2.x: use 'import * as' to get the full module namespace as VFS
import * as pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts; // raw TTF bytes

// Map font styles to the TTF filenames stored in the VFS
pdfMake.fonts = {
    Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf',
    }
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050';

export const fetchAuth = (endpoint, options = {}) => {
    const token = sessionStorage.getItem('token');
    const headers = { ...options.headers };

    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    if (token) {
        headers['x-auth-token'] = token;
    }

    if (options.body instanceof FormData) {
        delete headers['Content-Type'];
    }

    const finalUrl = endpoint.startsWith('http') ? endpoint : API_URL + endpoint;
    return fetch(finalUrl, { ...options, headers });
};

export const fetchAndDownload = async (url, filename) => {
    try {
        const res = await fetchAuth(url);
        if (!res.ok) throw new Error('Network response was not ok.');
        const blob = await res.blob();
        const href = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (err) {
        console.error('Download failed:', err);
    }
};

export const generatePdfBill = (saleDetails, billSettings) => {
    const { cart, customerName, customerPhone, total, invoiceNumber, showInvoiceOnBill } = saleDetails;
    const {
        billTitle = 'Bill of Supply',
        companyName = "Your Company",
        companyAddress = "Your Address",
        companyGstin = "Your GSTIN",
        companyContact = "Your Contact",
        companyLogo,
        isGstEnabled,
        gstPercentage,
        showHsn = true,
        showTerms = true,
        termsAndConditions = 'Goods once sold will not be taken back.\nThis is a computer generated invoice.',
        // Role-wise billing overrides (Feature 2)
        billingName,
        billingAddress,
        billingContact,
    } = billSettings || {};

    // Use per-user billing profile if set (Feature 2)
    const displayName = billingName || companyName;
    const displayAddress = billingAddress || companyAddress;
    const displayContact = billingContact || companyContact;

    const taxRate = isGstEnabled ? (parseInt(gstPercentage, 10) || 0) / 100 : 0;
    const subtotal = total / (1 + taxRate);
    const taxAmount = total - subtotal;
    const cgst = taxAmount / 2;
    const sgst = taxAmount / 2;

    const numberToWords = (num) => {
        const a = ['', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine ', 'ten ', 'eleven ', 'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '];
        const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
        if ((num = num.toString()).length > 9) return 'overflow';
        const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
        if (!n) return ''; var str = '';
        str += (n[1] !== 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'crore ' : '';
        str += (n[2] !== 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'lakh ' : '';
        str += (n[3] !== 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'thousand ' : '';
        str += (n[4] !== 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'hundred ' : '';
        str += (n[5] !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) + 'only ' : '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // Build table columns dynamically based on showHsn (Feature 1)
    const tableHeaderRow = [
        { text: 'Sr.', style: 'tableHeader' },
        { text: 'Item Description', style: 'tableHeader' },
        ...(showHsn ? [{ text: 'HSN/SAC', style: 'tableHeader' }] : []),
        { text: 'Qty', style: 'tableHeader' },
        { text: 'Rate', style: 'tableHeader' },
        { text: 'Amount', style: 'tableHeader' },
    ];

    const tableBody = [
        tableHeaderRow,
        ...cart.map((item, index) => [
            (index + 1).toString(),
            item.nameAtSale,
            ...(showHsn ? ['N/A'] : []),
            item.quantity.toString(),
            isGstEnabled ? `₹${(item.priceAtSale / (1 + taxRate)).toFixed(2)}` : `₹${item.priceAtSale.toFixed(2)}`,
            { text: isGstEnabled ? `₹${(item.priceAtSale * item.quantity / (1 + taxRate)).toFixed(2)}` : `₹${(item.priceAtSale * item.quantity).toFixed(2)}`, alignment: 'right' }
        ])
    ];

    const tableWidths = showHsn
        ? ['auto', '*', 'auto', 'auto', 'auto', 'auto']
        : ['auto', '*', 'auto', 'auto', 'auto'];

    // Build T&C section conditionally (Feature 1)
    const termsSection = showTerms ? {
        columns: [
            {
                stack: [
                    { text: 'Terms & Conditions:', style: 'fieldTitle' },
                    {
                        ul: (termsAndConditions || '').split('\n').filter(l => l.trim()),
                        fontSize: 8, margin: [0, 5, 0, 0]
                    }
                ]
            },
            { stack: [{ text: `For ${displayName}`, alignment: 'right' }, { text: '\n\n\nAuthorized Signatory', alignment: 'right', style: 'fieldTitle' }], margin: [0, 10, 0, 0] }
        ]
    } : {
        columns: [
            { text: '' },
            { stack: [{ text: `For ${displayName}`, alignment: 'right' }, { text: '\n\n\nAuthorized Signatory', alignment: 'right', style: 'fieldTitle' }], margin: [0, 10, 0, 0] }
        ]
    };

    const docDefinition = {
        content: [
            { text: billTitle || (isGstEnabled ? 'Tax Invoice' : 'Bill of Supply'), style: 'invoiceTitle', alignment: 'center' },
            {
                columns: [
                    { stack: [{ text: displayName, style: 'header' }, { text: displayAddress }, { text: `Contact: ${displayContact}` }, { text: `GSTIN: ${companyGstin}`, bold: true }], width: '*' },
                    companyLogo ? { image: companyLogo, width: 60, alignment: 'right' } : {}
                ],
                margin: [0, 0, 0, 10]
            },
            { canvas: [{ type: 'line', x1: 0, y1: 5, x2: 515, y2: 5, lineWidth: 1 }] },
            {
                columns: [
                    { stack: [{ text: 'Bill To:', style: 'fieldTitle' }, { text: customerName, bold: true }, { text: `Ph: ${customerPhone}` }] },
                    {
                        stack: [
                            showInvoiceOnBill !== false && invoiceNumber ? { text: `Invoice No: ${invoiceNumber}`, alignment: 'right' } : null,
                            { text: `Date: ${new Date().toLocaleDateString('en-IN')}`, alignment: 'right' }
                        ].filter(Boolean)
                    }
                ],
                margin: [0, 10, 0, 10]
            },
            { table: { headerRows: 1, widths: tableWidths, body: tableBody }, layout: 'lightHorizontalLines' },
            isGstEnabled ? {
                columns: [
                    { text: '' },
                    { stack: [{ text: 'Subtotal:' }, { text: `CGST @${gstPercentage / 2}%:` }, { text: `SGST @${gstPercentage / 2}%:` }, { text: 'Grand Total:', bold: true, fontSize: 12 }], alignment: 'right', width: 'auto', margin: [0, 10, 5, 0] },
                    { stack: [{ text: `₹${subtotal.toFixed(2)}` }, { text: `₹${cgst.toFixed(2)}` }, { text: `₹${sgst.toFixed(2)}` }, { text: `₹${total.toFixed(2)}`, bold: true, fontSize: 12 }], alignment: 'right', width: 'auto', margin: [0, 10, 0, 0] }
                ]
            } : {
                columns: [
                    { text: '' },
                    { text: 'Grand Total:', bold: true, fontSize: 12, alignment: 'right', width: 'auto', margin: [0, 10, 5, 0] },
                    { text: `₹${total.toFixed(2)}`, bold: true, fontSize: 12, alignment: 'right', width: 'auto', margin: [0, 10, 0, 0] }
                ]
            },
            { text: `Amount in Words: ${numberToWords(Math.round(total))}`, style: 'fieldTitle', margin: [0, 10, 0, 10] },
            { canvas: [{ type: 'line', x1: 0, y1: 5, x2: 515, y2: 5, lineWidth: 1 }] },
            termsSection,
        ],
        styles: {
            invoiceTitle: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 10] },
            header: { fontSize: 20, bold: true },
            fieldTitle: { fontSize: 9, bold: true, color: '#333333' },
            tableHeader: { bold: true, fontSize: 10, color: 'black', fillColor: '#eeeeee' },
        },
        defaultStyle: { fontSize: 10 }
    };
    pdfMake.createPdf(docDefinition).open();
};
